import React, { useState, useCallback, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import html2canvas from 'html2canvas';
import styles from './ProductCard.module.css';

// ── Texto para copiar ─────────────────────────────────────────────────────────

const buildCopyText = (p) => {
  const lines = [`*${p.nombre}*`];
  if (p.descripción)            lines.push(`\n${p.descripción}`);
  if (p.dimensiones_y_gabinete) lines.push(`📐 Dimensiones: ${p.dimensiones_y_gabinete}`);
  if (p.led_y_color)            lines.push(`💡 LED y Color: ${p.led_y_color}`);
  if (p.programacion)           lines.push(`⚙️ Programación: ${p.programacion}`);
  if (p.digitos)                lines.push(`🔢 Dígitos: ${p.digitos}`);
  if (p.sonido)                 lines.push(`🔊 Sonido: ${p.sonido}`);
  if (p.detalle)                lines.push(`ℹ️ ${p.detalle}`);
  lines.push(`\n📩 Consultá precio: https://microled.com.ar/contacto`);
  return lines.join('\n');
};

// ── Slider ────────────────────────────────────────────────────────────────────

const Slider = ({ fotos, nombre, onImgChange }) => {
  const [idx, setIdx]       = useState(0);
  const [zoom, setZoom]     = useState(null);
  const [loaded, setLoaded] = useState({});
  const touchX              = useRef(null);
  const total               = fotos.length;

  const go = useCallback((n) => {
    const next = ((n % total) + total) % total;
    setIdx(next);
    if (onImgChange) onImgChange(fotos[next]?.url || null);
  }, [total, fotos, onImgChange]);

  useEffect(() => {
    if (onImgChange && fotos[0]) onImgChange(fotos[0].url);
  }, [fotos, onImgChange]);

  const onKey = (e) => {
    if (e.key === 'ArrowLeft')  { e.preventDefault(); go(idx - 1); }
    if (e.key === 'ArrowRight') { e.preventDefault(); go(idx + 1); }
    if (e.key === 'Escape')     setZoom(null);
  };

  if (total === 0) return (
    <div className={styles.noImg}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
        <rect x="3" y="3" width="18" height="18" rx="2"/>
        <circle cx="8.5" cy="8.5" r="1.5"/>
        <polyline points="21 15 16 10 5 21"/>
      </svg>
      <span>Sin imagen</span>
    </div>
  );

  return (
    <>
      <div
        className={styles.sliderWrap}
        tabIndex={0}
        onKeyDown={onKey}
        onTouchStart={e => { touchX.current = e.touches[0].clientX; }}
        onTouchEnd={e => {
          if (touchX.current === null) return;
          const d = touchX.current - e.changedTouches[0].clientX;
          if (Math.abs(d) > 40) go(idx + (d > 0 ? 1 : -1));
          touchX.current = null;
        }}
        aria-label={`Imagen ${idx + 1} de ${total}`}
      >
        {!loaded[idx] && <div className={styles.skeleton} />}

        <img
          key={idx}
          src={fotos[idx].url}
          alt={fotos[idx].alt || nombre}
          className={`${styles.mainImg} ${loaded[idx] ? styles.imgIn : styles.imgOut}`}
          onClick={() => setZoom(fotos[idx])}
          onLoad={() => setLoaded(p => ({ ...p, [idx]: true }))}
          onError={() => setLoaded(p => ({ ...p, [idx]: true }))}
          draggable={false}
        />

        {total > 1 && (
          <>
            <button className={`${styles.arrow} ${styles.arrowL}`}
              onClick={e => { e.stopPropagation(); go(idx - 1); }} aria-label="Anterior">‹</button>
            <button className={`${styles.arrow} ${styles.arrowR}`}
              onClick={e => { e.stopPropagation(); go(idx + 1); }} aria-label="Siguiente">›</button>
            <span className={styles.badge}>{idx + 1} / {total}</span>
          </>
        )}
      </div>

      {total > 1 && (
        <div className={styles.thumbs}>
          {fotos.map((f, i) => (
            <button key={i}
              className={`${styles.thumb} ${i === idx ? styles.thumbOn : ''}`}
              onClick={() => setIdx(i)}
              aria-label={`Ver imagen ${i + 1}`}
            >
              <img src={f.url} alt="" draggable={false}
                onError={e => { e.target.style.opacity = '0.2'; }} />
            </button>
          ))}
        </div>
      )}

      {zoom && ReactDOM.createPortal(
        <div className={styles.zoomBg} onClick={() => setZoom(null)} role="dialog" aria-modal="true">
          <button className={styles.zoomX} onClick={() => setZoom(null)} aria-label="Cerrar">✕</button>
          <img src={zoom.url} alt={zoom.alt || nombre} className={styles.zoomImg}
            onClick={e => e.stopPropagation()} />
          {zoom.alt && <p className={styles.zoomCaption}>{zoom.alt}</p>}
        </div>,
        document.body
      )}
    </>
  );
};

// ── ProductCard ───────────────────────────────────────────────────────────────

const ATTRS = [
  { key: 'dimensiones_y_gabinete', icon: 'dimensiones',  label: 'Dimensiones'  },
  { key: 'digitos',                icon: 'alturaDigitos', label: 'Dígitos'      },
  { key: 'led_y_color',            icon: 'led',           label: 'LED y Color'  },
  { key: 'programacion',           icon: 'modificar',     label: 'Programación' },
  { key: 'sonido',                 icon: 'sonido',        label: 'Sonido'       },
  { key: 'detalle',                icon: 'detalle',       label: 'Detalles'     },
];

const ProductCard = ({ producto }) => {
  const [copyState, setCopyState] = useState('idle');
  const cardRef = useRef(null);

  const handleCopy = useCallback(async () => {
    if (copyState !== 'idle' || !cardRef.current) return;
    setCopyState('copying');
    try {
      const canvas = await html2canvas(cardRef.current, {
        useCORS: true,
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
      });
      const pngBlob = await new Promise(res => canvas.toBlob(res, 'image/png'));
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': pngBlob }),
      ]);
      setCopyState('done');
    } catch {
      setCopyState('error');
    }
    setTimeout(() => setCopyState('idle'), 2200);
  }, [copyState]);

  if (!producto) return null;

  const fotos = (producto.photos || [])
    .flatMap(g => g.url.map((u, i) => ({ url: u, alt: g.alt?.[i] || '' })))
    .filter(f => f.url);

  const attrs = ATTRS.filter(a => producto[a.key]);

  return (
    <article className={styles.card} ref={cardRef}>

      {/* Botón copiar — top-right absoluto */}
      <button
        className={`${styles.copyBtn} ${copyState !== 'idle' ? styles.copyBtnActive : ''}`}
        onClick={handleCopy}
        aria-label="Copiar imagen como PNG"
      >
        <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6">
          <rect x="6" y="6" width="10" height="10" rx="2"/>
          <path d="M13 6V4a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h2"/>
        </svg>
        <span className={styles.copyLabel}>
          {copyState === 'done' ? '✓ Copiado' : copyState === 'error' ? 'Error' : 'Copiar'}
        </span>
      </button>

      {/* Columna imagen */}
      <div className={styles.imgCol}>
        <Slider fotos={fotos} nombre={producto.nombre} />
      </div>

      {/* Columna info */}
      <div className={styles.infoCol}>

        <div className={styles.accent} />

        <div className={styles.titleRow}>
          <h2 className={styles.name}>{producto.nombre}</h2>
        </div>

        {producto.descripción && (
          <p className={styles.desc}>{producto.descripción}</p>
        )}

        {/* Atributos */}
        {attrs.length > 0 && (
          <ul className={styles.attrs}>
            {attrs.map(({ key, icon, label }) => (
              <li key={key} className={styles.attr}>
                <img
                  src={`/Assets/icons/ProductCard/${icon}.svg`}
                  alt=""
                  className={styles.attrIcon}
                />
                <span className={styles.attrBody}>
                  <strong>{label} </strong>
                  {producto[key]}
                </span>
              </li>
            ))}
          </ul>
        )}

      </div>
    </article>
  );
};

export default ProductCard;
